import React from 'react'
import circleHome from './circleHome.svg'
import { OnboardingOverlay } from 'common/OnboardingOverlay'
import { TwoCTAFooter } from 'common/styleguide/Modal'
import { CTA } from 'common/styleguide/CTA'
import { DASH_LAUNCH } from 'lib/utils'
import { Msg, useFormatMessage, commonCTAs } from 'lib/localization'
import { FormattedMessage as T, defineMessages } from 'react-intl'

const messages = defineMessages({
  maybeNew: {
    id: 'DashboardOnboardingOverlay.maybeNew',
    defaultMessage: 'new'
  }
})

export const DashboardOnboardingOverlay = ({ currentUser, ...props }) => {
  const fm = useFormatMessage()
  // "legacy user" in this context referrs to a user who was previously used to
  // seeing the Classic Profile page as their home page for the site.
  // legacy users see a slightly different set of onboarding steps that helps
  // explain the homepage in the context of a redesign of an existing feature,
  // as opposed to the content we show when onboarding a brand-new user
  const legacyUser = currentUser.createdAt < DASH_LAUNCH

  const introStep = {
    heading: <span>
      <T
        id='onboardingMessages.introHeading'
        defaultMessage='Welcome to your{maybeNew} Ride with GPS homepage'
        values={{ maybeNew: legacyUser ? ` ${fm(messages.maybeNew)}` : '' }} />
    </span>,
    content: <div>
      <img src={circleHome} height='100' width='100' style={{ marginBottom: '30px', marginTop: '20px' }} />
      {legacyUser
        ? <p>
          <T id='onboardingMessages.legacyUserContent'
            defaultMessage="With the new homepage, it's easier to access {break}the data and features you use most"
            values={{ break: <br /> }} />
        </p>
        : <p>
          <T id='onboardingMessages.newUserContent'
            defaultMessage="This is your hub for everything Ride with GPS. Let's take a quick 1-minute tour to get you acquainted with the most important features." />
        </p>}
    </div>,
    footer: next => <TwoCTAFooter>
      <a onClick={props.onRequestClose}><Msg capitalized m={commonCTAs.skip} /></a>
      <CTA inline margins={false} onClick={next}>
        <T id='onboardingMessages.showMeAround' defaultMessage='Show Me Around' />
      </CTA>
    </TwoCTAFooter>
  }

  const onboardingSteps = [
    {
      heading: <T id='onboardingSteps.header1' defaultMessage='The Ride with GPS Dashboard: your quick access hub' />,
      content: <p>
        <T id='onboardingSteps.content1'
          defaultMessage='Access all your most important data and stats with one click.' />
      </p>,
      targetId: 'dashboard-nav-home'
    },
    {
      heading: <T id='onboardingSteps.header2' defaultMessage='Your routes, rides, and more' />,
      content: <p>
        <T id='onboardingSteps.content2'
          defaultMessage='Click to quickly access your calendar, routes, and rides, or to upload your files.' />
      </p>,
      targetId: 'side-nav-links'
    },
    {
      heading: <T id='onboardingSteps.header3' defaultMessage='Analyze your rides' />,
      content: <p>
        <T id='onboardingSteps.content3'
          defaultMessage='The Ride Center provides powerful tools to analyze your rides and manage your ride data in bulk.' />
      </p>,
      targetId: 'dashboard-nav-analyze'
    },
    {
      heading: <T id='onboardingSteps.header4' defaultMessage="Connect with friends and see what they're up to" />,
      content: <p>
        <T id='onboardingSteps.content4'
          defaultMessage='See your friends list and what your friends are riding or planning.' />
      </p>,
      targetId: 'dashboard-nav-friends'
    },
    {
      heading: <T id='onboardingSteps.header5' defaultMessage='Access your photos, segments, and profile' />,
      content: <p>
        <T id='onboardingSteps.content5'
          defaultMessage='All of your account details, photos, segments, gear, and profile info now live in one place.' />
      </p>,
      targetId: 'dashboard-nav-more'
    },
    legacyUser && {
      heading: <T id='onboardingSteps.header6' defaultMessage='Return to the Ride with GPS classic profile any time' />,
      content: <p>
        <T id='onboardingSteps.content6'
          defaultMessage='You can return to the classic profile by clicking the highlighted link.' />
      </p>,
      targetId: 'dashboard-nav-classic-profile'
    },
    {
      heading: <T id='onboardingSteps.header7' defaultMessage='Send feedback to our support team at any time' />,
      content: <p>
        <T id='onboardingSteps.content7'
          defaultMessage='Have questions or comments about the new homepage?
      Get in touch with Ride with GPS support via our Help portal, or by clicking
      the blue help button.' />
      </p>,
      targetId: 'global-nav-help'
    }
  ].filter(v => v)

  return <OnboardingOverlay
    {...props}
    introStep={introStep}
    steps={onboardingSteps} />
}
